import { useEffect, useState } from 'react';
import './App.css';

const AGENT_STUDIO_URL = 'https://agentstudio-demo.cortexweave.ai/';
const ACCOUNT_ACCESS_EMAIL =
  'mailto:contact@cortexweave.ai?subject=Agent%20Studio%20Account%20Access&body=Hi%20CortexWeave%20team%2C%0A%0AI%27d%20like%20access%20to%20Agent%20Studio.%20Please%20create%20an%20account%20for%20me.%0A%0AName%3A%0ACompany%3A%0AEmail%3A';

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.fade-in-up').forEach((el) => observer.observe(el));

    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  const scrollTo = (e, id) => {
    e.preventDefault();
    setMenuOpen(false);
    if (id === '#') return;
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="app">
      {/* ─── HEADER ─── */}
      <header className={`header${scrolled ? ' scrolled' : ''}`}>
        <div className="header-inner">
          <a href="/" className="logo" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src="/CortexWeaveAIBright.png" alt="CortexWeave AI" />
            <span className="logo-text">CortexWeave AI</span>
          </a>
          <nav className="desktop-nav">
            <a href="#agent-studio" className="nav-link" onClick={(e) => scrollTo(e, '#agent-studio')}>Agent Studio</a>
            <a href="#features" className="nav-link" onClick={(e) => scrollTo(e, '#features')}>Features</a>
            <a href="#architecture" className="nav-link" onClick={(e) => scrollTo(e, '#architecture')}>Architecture</a>
            <a href="#how-it-works" className="nav-link" onClick={(e) => scrollTo(e, '#how-it-works')}>How It Works</a>
            <a href={AGENT_STUDIO_URL} className="nav-cta" target="_blank" rel="noreferrer">Launch Agent Studio</a>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu">
            <span className={`hamburger${menuOpen ? ' open' : ''}`} />
          </button>
        </div>
      </header>

      {/* ─── MOBILE MENU ─── */}
      {menuOpen && (
        <div className="mobile-menu">
          <a href="#agent-studio" className="mobile-nav-link" onClick={(e) => scrollTo(e, '#agent-studio')}>Agent Studio</a>
          <a href="#features" className="mobile-nav-link" onClick={(e) => scrollTo(e, '#features')}>Features</a>
          <a href="#architecture" className="mobile-nav-link" onClick={(e) => scrollTo(e, '#architecture')}>Architecture</a>
          <a href="#how-it-works" className="mobile-nav-link" onClick={(e) => scrollTo(e, '#how-it-works')}>How It Works</a>
          <a href={AGENT_STUDIO_URL} className="mobile-nav-cta" target="_blank" rel="noreferrer">Launch Agent Studio</a>
          <a href={ACCOUNT_ACCESS_EMAIL} className="mobile-nav-link mobile-nav-secondary">Request Account Access</a>
        </div>
      )}

      {/* ─── HERO ─── */}
      <section className="hero hero-gradient">
        <div className="grid-bg" />
        <div className="orb orb-1" />
        <div className="orb orb-2" />
        <div className="orb orb-3" />
        <div className="hero-container">
          <div className="hero-content">
            <span className="hero-badge">
              <span className="badge-dot" />
              MVP Live &middot; Private Beta
            </span>
            <h1 className="hero-title">
              Build <span className="gradient-text">Predictable AI Agents</span> at Enterprise Scale
            </h1>
            <p className="hero-subtitle">
              CortexWeave AI is the agentic platform for teams that need control. Build, test, and deploy deterministic or regular AI agents through <strong>Agent Studio</strong>&nbsp;&mdash; our visual development environment.
            </p>
            <div className="hero-buttons">
              <a href={AGENT_STUDIO_URL} className="btn-primary" target="_blank" rel="noreferrer">
                Launch Agent Studio
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
              </a>
              <a href={ACCOUNT_ACCESS_EMAIL} className="btn-secondary">
                Request Account Access
              </a>
            </div>
          </div>
          <div className="hero-visual fade-in-up">
            <div className="browser-frame">
              <div className="browser-header">
                <div className="browser-dots">
                  <span className="dot dot-red" />
                  <span className="dot dot-yellow" />
                  <span className="dot dot-green" />
                </div>
                <div className="browser-url">cortexweave.ai/studio</div>
              </div>
              <div className="browser-body">
                <img src="/CortexWeaveAgentDashboard.png" alt="Agent Studio" loading="lazy" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <div className="stat-item fade-in-up">
              <div className="stat-number">80%</div>
              <div className="stat-label">Token Reduction</div>
            </div>
            <div className="stat-item fade-in-up">
              <div className="stat-number">10x</div>
              <div className="stat-label">Faster Development</div>
            </div>
            <div className="stat-item fade-in-up">
              <div className="stat-number">100%</div>
              <div className="stat-label">Git Native</div>
            </div>
            <div className="stat-item fade-in-up">
              <div className="stat-number">0</div>
              <div className="stat-label">Hallucinations</div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── AGENT STUDIO ─── */}
      <section className="studio-section" id="agent-studio">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Agent Studio</span>
            <h2 className="section-title">Your AI Development Environment</h2>
            <p className="section-subtitle">Agent Studio gives you full control over every aspect of your AI agents. Deterministic workflows, a proprietary optimizer, and structured outputs&nbsp;&mdash; no black boxes.</p>
          </div>
          <div className="studio-grid">
            <div className="studio-card fade-in-up">
              <div className="studio-icon">
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <h3 className="studio-card-title">Deterministic Workflows</h3>
              <p className="studio-card-desc">Define multi-step agents with clear, deterministic flows. Eliminate ambiguity for mission-critical tasks. Create complex internal tools with full dependency and library management.</p>
            </div>
            <div className="studio-card fade-in-up">
              <div className="studio-icon">
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <h3 className="studio-card-title">Optimizer Engine</h3>
              <p className="studio-card-desc">Only sends a single relevant step to the LLM per turn&nbsp;&mdash; 80% reduction in token usage. DAG-based optimizer handles summary optimization automatically.</p>
            </div>
            <div className="studio-card fade-in-up">
              <div className="studio-icon">
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <h3 className="studio-card-title">Zero Hallucination</h3>
              <p className="studio-card-desc">Pre and post processing of data with deterministic outputs for structured workflows. Internal tools reduce latency while keeping responses grounded.</p>
            </div>
          </div>
          <div className="studio-actions fade-in-up">
            <a href={AGENT_STUDIO_URL} className="btn-primary" target="_blank" rel="noreferrer">
              Open Agent Studio
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/></svg>
            </a>
            <a href={ACCOUNT_ACCESS_EMAIL} className="btn-secondary">
              Need an account?
            </a>
          </div>
        </div>
      </section>

      {/* ─── PLATFORM PREVIEW ─── */}
      <section className="platform-section" id="platform">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Inside Agent Studio</span>
            <h2 className="section-title">A Glimpse of What You Get</h2>
          </div>
          <div className="preview-grid">
            <div className="preview-card preview-wide fade-in-up">
              <div className="preview-img-wrap">
                <img src="/CortexWeaveStateMachine.png" alt="State Machine Builder" loading="lazy" />
                <div className="preview-fade" />
              </div>
              <div className="preview-meta">
                <span className="preview-icon">
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
                </span>
                <span className="preview-label">State Machine Builder</span>
              </div>
            </div>
            <div className="preview-card fade-in-up">
              <div className="preview-img-wrap">
                <img src="/CortexWeaveChat.png" alt="Agent Testing" loading="lazy" />
                <div className="preview-fade" />
              </div>
              <div className="preview-meta">
                <span className="preview-icon">
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>
                </span>
                <span className="preview-label">Multi-Modal Testing</span>
              </div>
            </div>
            <div className="preview-card fade-in-up">
              <div className="preview-img-wrap">
                <img src="/CortexWeaveTools.png" alt="Custom Tools" loading="lazy" />
                <div className="preview-fade" />
              </div>
              <div className="preview-meta">
                <span className="preview-icon">
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                </span>
                <span className="preview-label">Custom Tools</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="features-section" id="features">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Features</span>
            <h2 className="section-title">Everything You Need to Ship AI Agents</h2>
            <p className="section-subtitle">A complete test-first development lifecycle with full Git integration, RBAC, and deployment to any cloud.</p>
          </div>
          <div className="features-grid">
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                </svg>
              </div>
              <h3 className="feature-title">Git Native</h3>
              <p className="feature-description">Full developer control with Git-based version control. Toggle between UI and code view. Navigate entire project structure including config, prompts, and tools.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="feature-title">Test-First Development</h3>
              <p className="feature-description">Instantly test your agent using built-in test tools. Test in text or voice within the UI with full TTS support.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"/>
                </svg>
              </div>
              <h3 className="feature-title">Visual Workflow Builder</h3>
              <p className="feature-description">Drag-and-drop canvas for building deterministic workflows. Define multi-step agents with animated transitions, editable nodes, and real-time data sync.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
              </div>
              <h3 className="feature-title">Enterprise Security</h3>
              <p className="feature-description">RBAC with authentication and authorization. Built-in secrets library using envelope encryption to keep sensitive data safe.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
                </svg>
              </div>
              <h3 className="feature-title">One-Click Deploy</h3>
              <p className="feature-description">Complete development lifecycle from git branch to production. Deploy via Kubernetes manifests to any cloud provider.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                </svg>
              </div>
              <h3 className="feature-title">Multi-Agent Systems</h3>
              <p className="feature-description">Hierarchical agent architecture with root and sub-agents. Orchestrate complex multi-agent workflows across deterministic and conversational agent types.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper icon-accent">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>
                </svg>
              </div>
              <h3 className="feature-title">State Machine Orchestration</h3>
              <p className="feature-description">Visual state machine builder with conditional routing, start/end nodes, and animated transitions. Build advanced agent orchestration with branching logic.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper icon-accent">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"/>
                </svg>
              </div>
              <h3 className="feature-title">Voice &amp; Multi-Modal</h3>
              <p className="feature-description">Built-in speech-to-text and text-to-speech with real-time streaming responses. Test agents with voice using configurable voices.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper icon-accent">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </div>
              <h3 className="feature-title">Real-Time Analytics</h3>
              <p className="feature-description">Monitor token usage, response times, and tool execution in real time. Performance dashboards with per-message metrics and historical tracking.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper icon-accent">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>
                </svg>
              </div>
              <h3 className="feature-title">Explorer Mode</h3>
              <p className="feature-description">Full developer control with a tree-based file explorer, built-in Monaco code editor, and dual-view sidebar. Toggle between simplified agent tiles and detailed project navigation.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper icon-accent">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"/>
                </svg>
              </div>
              <h3 className="feature-title">Knowledge Base &amp; RAG</h3>
              <p className="feature-description">Upload files and curate data sources for your AI agents. Integrated RAG system lets agents retrieve context from your own documents and knowledge repositories.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper icon-accent">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9.75A1.75 1.75 0 014.75 8h14.5A1.75 1.75 0 0121 9.75v7.5A1.75 1.75 0 0119.25 19H4.75A1.75 1.75 0 013 17.25v-7.5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 8V6.75A1.75 1.75 0 0110.75 5h2.5A1.75 1.75 0 0115 6.75V8" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12h7.5m3 0H21" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.5 12h3v2h-3z" />
                </svg>
              </div>
              <h3 className="feature-title">Tools</h3>
              <p className="feature-description">Agents can seamlessly leverage internal Python tools (packaged with the runtime), external APIs with built-in testing, and MCP tools—all directly within their workflows.</p>
            </div>
            <div className="feature-card fade-in-up">
              <div className="feature-icon-wrapper icon-accent">
                <svg className="feature-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                </svg>
              </div>
              <h3 className="feature-title">Callback Hooks</h3>
              <p className="feature-description">Fine-grained control with before/after hooks for agent, model, and tool execution. Inject custom logic at every stage of the agent lifecycle.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── ARCHITECTURE ─── */}
      <section className="architecture-section" id="architecture">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Architecture</span>
            <h2 className="section-title">Lean &amp; Powerful</h2>
            <p className="section-subtitle">Our proprietary agentic engine sits at the core, powering everything from workflow optimization to agent orchestration.</p>
          </div>
          <div className="arch-card fade-in-up">
            <div className="arch-diagram">
              <div className="arch-layer">
                <div className="arch-layer-title">Agent Studio</div>
                <div className="arch-layer-desc">Visual workflow editor, agent configuration, testing interface</div>
              </div>
              <div className="arch-connector"><span /></div>
              <div className="arch-layer">
                <div className="arch-layer-title">Backend Services</div>
                <div className="arch-layer-desc">Code wiring, agent instantiation, session management</div>
              </div>
              <div className="arch-connector"><span /></div>
              <div className="arch-layer core">
                <div className="arch-layer-title" style={{ fontSize: '1.25rem' }}>Agentic Engine + Optimizer</div>
                <div className="arch-layer-desc">Workflow parsing, token optimization, DAG-based summaries</div>
              </div>
              <div className="arch-connector"><span /></div>
              <div className="arch-layer">
                <div className="arch-layer-title">RAG System</div>
                <div className="arch-layer-desc">Curate data for AI agents from your own data sources</div>
              </div>
              <div className="arch-connector"><span /></div>
              <div className="arch-layer">
                <div className="arch-layer-title">Cloud Infrastructure</div>
                <div className="arch-layer-desc">Kubernetes deployment, GCP integration, auto-scaling</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="how-it-works" id="how-it-works">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">How It Works</span>
            <h2 className="section-title">From Branch to Production</h2>
            <p className="section-subtitle">A test-first development lifecycle built around Git.</p>
          </div>
          <div className="steps-grid">
            <div className="step-card fade-in-up">
              <div className="step-number">1</div>
              <h3 className="step-title">Create Branch</h3>
              <p className="step-description">Everything starts with a git branch. All changes are version controlled from day one.</p>
            </div>
            <div className="step-card fade-in-up">
              <div className="step-number">2</div>
              <h3 className="step-title">Build Agent</h3>
              <p className="step-description">Design workflows in Agent Studio or code. Define steps, tools, and agent behavior with visual builders.</p>
            </div>
            <div className="step-card fade-in-up">
              <div className="step-number">3</div>
              <h3 className="step-title">Test &amp; Iterate</h3>
              <p className="step-description">Test in text or voice with real-time streaming. Iterate until your agent behaves exactly as expected.</p>
            </div>
            <div className="step-card fade-in-up">
              <div className="step-number">4</div>
              <h3 className="step-title">Ship It</h3>
              <p className="step-description">One-click deploy to any cloud via Kubernetes manifests.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="cta-section">
        <div className="cta-bg" />
        <div className="container cta-content">
          <h2 className="cta-title">Ready to Build Predictable AI Agents?</h2>
          <p className="cta-subtitle">Agent Studio is live for approved accounts. Request early account access and start building with the platform.</p>
          <div className="cta-actions">
            <a href={ACCOUNT_ACCESS_EMAIL} className="btn-primary">Request Account Access</a>
            <a href={AGENT_STUDIO_URL} className="btn-secondary" target="_blank" rel="noreferrer">Open Agent Studio</a>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="footer">
        <div className="container">
          <div className="footer-simple">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/CortexWeaveAIBright.png" alt="CortexWeave AI" />
                <span className="footer-logo-text">CortexWeave AI</span>
              </div>
              <p className="footer-description">Building the future of enterprise AI agents.</p>
            </div>
            <div>
              <h4 className="footer-title">Product</h4>
              <ul className="footer-links">
                <li><a href="#agent-studio">Agent Studio</a></li>
                <li><a href={AGENT_STUDIO_URL} target="_blank" rel="noreferrer">Open Agent Studio</a></li>
                <li><a href={ACCOUNT_ACCESS_EMAIL}>Request Account Access</a></li>
                <li><a href="#features">Features</a></li>
                <li><a href="#architecture">Architecture</a></li>
              </ul>
            </div>
            <div>
              <h4 className="footer-title">Services</h4>
              <ul className="footer-links">
                <li><span>Professional Services</span></li>
              </ul>
              <p className="footer-services-desc">Want us to build your agents for you? Our team can design, build, and deploy custom AI agents tailored to your business.</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 CortexWeave AI. All rights reserved.</p>
            <div className="footer-bottom-links">
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
